import {
  PromptFillJobInputSchema,
  type PromptFillJobOutput,
} from '@/entities/jobs/promptFill';
import { ResearchJobOutputSchema } from '@/entities/jobs/research';
import { fillTemplate } from '@/features/prompt-generation/promptFill';
import { newId } from '@/shared/lib/id';
import { predictionActualCost } from '@/shared/pricing';
import type { Runner, RunnerResult } from './types';
import { uploadProductImages } from './uploadProductImages';

export const promptFillRunner: Runner = async (
  job,
  ctx,
): Promise<RunnerResult<PromptFillJobOutput>> => {
  const input = PromptFillJobInputSchema.parse(job.input);
  const dna = input.dna
    ? input.dna
    : (await ctx.getJobOutput(input.researchJobId as string, ResearchJobOutputSchema)).dna;

  const imageUrls =
    input.template.needsProductImages &&
    input.productImageIds &&
    input.productImageIds.length > 0
      ? await uploadProductImages(ctx, input.brandId, input.productImageIds)
      : [];

  const filled = await fillTemplate(ctx.client, {
    template: input.template,
    dna,
    brandName: input.brandName,
    llmModelId: input.llmModelId,
    ...(input.conceptBrief && { conceptBrief: input.conceptBrief }),
    ...(imageUrls.length > 0 && { imageUrls }),
    signal: ctx.signal,
  });

  const resolved = { ...filled.resolved, numImages: input.numImages };

  const rowId = newId('rp');
  await ctx.db.resolvedPrompts.add({
    ...resolved,
    id: rowId,
    brandId: input.brandId,
    jobId: job.id,
    createdAt: Date.now(),
  });

  const actualCostUsd = predictionActualCost({
    modelSlug: input.llmModelId,
    kind: 'llm',
    ...(filled.inputTokens !== undefined && { inputTokens: filled.inputTokens }),
    ...(filled.outputTokens !== undefined && { outputTokens: filled.outputTokens }),
    ...(filled.predictTimeSec !== undefined && { predictTimeSec: filled.predictTimeSec }),
  });

  return {
    output: { resolved, resolvedPromptRowId: rowId },
    replicateId: filled.replicateId,
    usage: {
      ...(filled.inputTokens !== undefined && { inputTokens: filled.inputTokens }),
      ...(filled.outputTokens !== undefined && { outputTokens: filled.outputTokens }),
      ...(filled.predictTimeSec !== undefined && { predictTimeSec: filled.predictTimeSec }),
    },
    actualCostUsd,
  };
};
